import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUserSignal } from '@adornis/users/client/currentUser.js';
import { html, nothing, type TemplateResult } from 'lit';
import { answerNotification } from '../../features/notifications/api/answerNotification.js';
import { confirmAnswerInterpretation } from '../../features/notifications/api/confirmAnswerInterpretation.js';
import { markNotificationRead } from '../../features/notifications/api/markNotificationRead.js';
import { setNotificationStatus } from '../../features/notifications/api/setNotificationStatus.js';
import { subscribeAllNotifications } from '../../features/notifications/api/subscribeAllNotifications.js';
import { subscribeMyNotifications } from '../../features/notifications/api/subscribeMyNotifications.js';
import { mayIManageUsers } from '../../features/user-management/api/mayIManageUsers.js';
import { NotificationAnswerForm } from './NotificationAnswerForm.js';
import {
  BLOCKING_CATEGORIES,
  NOTIFICATION_CATEGORY_DESCRIPTIONS,
  NOTIFICATION_CATEGORY_DOTS,
  NOTIFICATION_CATEGORY_LABELS,
  NotificationStatus,
  type UserNotification,
} from '../../features/notifications/db/UserNotification.js';

/**
 * The cross-request overview: everything the platform wants from this user, in one place.
 *
 * Answering a single question is better done in the request itself (see `RequestQuestions`),
 * where the position it is about is visible and a whole request is settled in one pass. What
 * this view is for is the question the request view cannot answer — *which* of my requests is
 * stuck, and on what. It is also the only home for a technical abort, whose request may not
 * even have an extraction to open.
 *
 * Grouped by request for the same reason: five questions about one inquiry are one piece of
 * work, not five.
 *
 * Every field the render reads is requested below. A field left out of a selection set does
 * not degrade the view, it makes the whole query throw.
 */
const SELECTION_SET = {
  _id: 1,
  recipientUserID: 1,
  recipientEmailSnapshot: 1,
  category: 1,
  severity: 1,
  status: 1,
  title: 1,
  contextLabel: 1,
  body: 1,
  mailID: 1,
  positionNumber: 1,
  readAt: 1,
  createdAt: 1,
  raiseCount: 1,
  allowsFreeText: 1,
  options: { _id: 1, key: 1, label: 1, confirms: 1 },
  answer: {
    _id: 1,
    selectedOptionKey: 1,
    freeText: 1,
    answeredAt: 1,
    channel: 1,
    outcomeSummary: 1,
    rejections: 1,
    interpretation: { _id: 1, summary: 1, confidence: 1, needsConfirmation: 1, applied: 1 },
  },
} as const;

const categoryLabel = (notification: UserNotification) => NOTIFICATION_CATEGORY_LABELS[notification.category];
const categoryDot = (notification: UserNotification) => NOTIFICATION_CATEGORY_DOTS[notification.category];

const isClosed = (notification: UserNotification) =>
  notification.status === NotificationStatus.RESOLVED || notification.status === NotificationStatus.DISMISSED;

const isBlocking = (notification: UserNotification) => BLOCKING_CATEGORIES.includes(notification.category);

const pendingReading = (notification: UserNotification) =>
  notification.answer?.interpretation?.needsConfirmation ? notification.answer.interpretation : null;

/**
 * One entry per request, blocked ones first. The label is the mail subject; notifications
 * without one (a technical abort before the subject could be read) collect under a fallback
 * rather than each forming a group of their own.
 */
interface RequestGroup {
  key: string;
  label: string;
  /** Whose questions these are — only shown in the administrator's all-users view. */
  recipient: string;
  items: UserNotification[];
  blocking: number;
}

function groupByRequest(list: UserNotification[]): RequestGroup[] {
  const groups = new Map<string, RequestGroup>();
  for (const notification of list) {
    // The recipient is part of the key, not just of the label: in the all-users view two
    // people's requests without a mail id would otherwise land in one another's group.
    const key = `${notification.mailID ?? 'ohne-anfrage'}::${notification.recipientUserID}`;
    const existing = groups.get(key);
    const group =
      existing ??
      ({
        key,
        label: notification.contextLabel?.trim() || 'Ohne erkennbaren Betreff',
        recipient: notification.recipientEmailSnapshot ?? '',
        items: [],
        blocking: 0,
      } satisfies RequestGroup);
    group.items.push(notification);
    if (isBlocking(notification)) group.blocking += 1;
    groups.set(key, group);
  }
  // Blocked requests first — they cost money for as long as they sit there.
  return [...groups.values()].sort((a, b) => b.blocking - a.blocking);
}

const formatDate = (date: Date | null | undefined) =>
  date ? `${date.toLocaleDateString()}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '';

const emptyState = (showDone: boolean, allUsers: boolean) => html`
  <div class="h-full flex flex-col items-center justify-center text-center gap-3 text-base-content/40 p-8">
    <x-icon class="text-5xl">${showDone ? 'inventory_2' : 'task_alt'}</x-icon>
    <div class="text-base font-medium text-base-content/60">
      ${showDone ? 'Noch nichts abgeschlossen' : 'Keine offenen Rückfragen'}
    </div>
    <div class="text-sm max-w-xs">
      ${showDone
        ? 'Erledigte und verworfene Meldungen sammeln sich hier.'
        : allUsers
          ? 'Zurzeit wartet in keiner Anfrage eine Rückfrage — quer über alle Nutzer.'
          : 'Sobald eine Anfrage nicht durchläuft oder eine Angabe fehlt, erscheint sie hier.'}
    </div>
  </div>
`;

function renderRow(notification: UserNotification, isActive: boolean, onSelect: () => void): TemplateResult {
  const isUnread = !notification.readAt;
  return html`
    <button
      class="w-full text-left pl-5 pr-4 py-2.5 flex gap-3 hover:bg-base-200/60 ${isActive ? 'bg-primary/10' : ''}"
      @click=${onSelect}
    >
      <span
        class="mt-1.5 w-2 h-2 rounded-full shrink-0"
        style="background:${categoryDot(notification)}"
        title=${categoryLabel(notification)}
      ></span>
      <span class="min-w-0 flex-1">
        <span class="block text-sm truncate ${isUnread ? 'font-semibold' : 'font-normal'}">${notification.title}</span>
        <span class="flex items-center gap-1.5 text-xs text-base-content/40 mt-0.5">
          <span class="truncate">${categoryLabel(notification)} · ${formatDate(notification.createdAt)}</span>
          ${pendingReading(notification)
            ? html`<span class="badge badge-xs badge-info shrink-0">Bestätigen</span>`
            : notification.status === NotificationStatus.ANSWERED
              ? html`<span class="badge badge-xs badge-ghost shrink-0">Beantwortet</span>`
              : nothing}
        </span>
      </span>
    </button>
  `;
}

/** The request a set of questions belongs to — the heading its rows sit under. */
function renderGroupHeader(group: RequestGroup, showRecipient: boolean): TemplateResult {
  return html`
    <div class="px-4 pt-3 pb-1.5 border-t border-base-200 bg-base-200/30">
      <div class="flex items-center gap-2">
        <x-icon class="text-sm text-base-content/40">mail</x-icon>
        <span class="text-xs font-semibold truncate flex-1 text-base-content/70">${group.label}</span>
        ${group.blocking
          ? html`<span class="badge badge-xs badge-error shrink-0" title="Blockiert die Übermittlung an SAP">
              ${group.blocking}× blockiert
            </span>`
          : nothing}
      </div>
      ${showRecipient && group.recipient
        ? html`<div class="flex items-center gap-1 text-[11px] text-base-content/45 pl-6 mt-0.5">
            <x-icon class="text-xs">person</x-icon><span class="truncate">${group.recipient}</span>
          </div>`
        : nothing}
    </div>
  `;
}

/**
 * The last answer and, just as importantly, what came of it.
 *
 * A question can carry an answer and still be open — an ambiguous customer name, a material
 * number that exists nowhere. Showing only the answer under the heading "Beantwortet" left the
 * reader to wonder why the question was still there. The outcome line is the missing half.
 */
function renderExistingAnswer(notification: UserNotification): TemplateResult | typeof nothing {
  const answer = notification.answer;
  if (!answer) return nothing;
  const chosen = (notification.options ?? []).find(option => option.key === answer.selectedOptionKey);
  const stillOpen = notification.status === NotificationStatus.OPEN;
  return html`
    <div class="rounded-2xl bg-base-200/50 border border-base-200 mt-4 p-5">
      <div class="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2">
        ${stillOpen ? 'Letzte Antwort' : 'Beantwortet'} ${formatDate(answer.answeredAt)}
      </div>
      ${chosen ? html`<div class="text-sm font-medium">${chosen.label}</div>` : nothing}
      ${answer.freeText ? html`<div class="text-sm whitespace-pre-line mt-1">${answer.freeText}</div>` : nothing}
      ${answer.outcomeSummary
        ? html`<div class="text-xs text-base-content/60 mt-2 flex items-start gap-1.5">
            <x-icon class="text-sm text-base-content/40">arrow_forward</x-icon><span>${answer.outcomeSummary}</span>
          </div>`
        : nothing}
      ${answer.rejections?.length
        ? html`<div class="mt-2 rounded-lg bg-error/5 px-3 py-2">
            <div class="text-xs font-semibold text-error/80">Nicht übernommen — die Frage bleibt offen</div>
            <ul class="list-disc pl-4 text-xs text-base-content/65 mt-0.5">
              ${answer.rejections.map(reason => html`<li>${reason}</li>`)}
            </ul>
          </div>`
        : nothing}
    </div>
  `;
}

function renderDetailHeader(notification: UserNotification): TemplateResult {
  const repeated = (notification.raiseCount ?? 1) > 1;
  return html`
    <div class="flex flex-wrap items-center gap-2 mb-2">
      <span class="w-2 h-2 rounded-full shrink-0" style="background:${categoryDot(notification)}"></span>
      <span class="text-xs font-semibold uppercase tracking-wide text-base-content/50">
        ${categoryLabel(notification)}
      </span>
      ${isClosed(notification) ? html`<span class="badge badge-sm badge-ghost">${notification.status}</span>` : nothing}
      ${repeated
        ? html`<span class="badge badge-sm badge-ghost" title="Seit dem ersten Auftreten erneut gemeldet">
            ${notification.raiseCount}× aufgetreten
          </span>`
        : nothing}
    </div>
  `;
}

/** What the detail pane needs from the page — lifted out to keep the component body legible. */
interface DetailContext {
  busy: boolean;
  onStatus: (status: NotificationStatus) => void;
  onAnswer: (optionKey: string | null, freeText: string) => void;
  onInterpretation: (accept: boolean) => void;
}

function renderDetail(notification: UserNotification, ctx: DetailContext): TemplateResult {
  const { busy } = ctx;
  return html`
    <div class="p-6 max-w-3xl">
      ${renderDetailHeader(notification)}
      <h1 class="text-xl font-bold tracking-tight">${notification.title}</h1>
      ${notification.contextLabel
        ? html`<p class="text-sm text-base-content/70 mt-1">${notification.contextLabel}</p>`
        : nothing}
      <p class="text-sm text-base-content/60 mt-1">${NOTIFICATION_CATEGORY_DESCRIPTIONS[notification.category]}</p>

      <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm mt-5 p-5">
        <div class="text-sm whitespace-pre-line leading-relaxed">${notification.body}</div>
      </div>

      ${renderExistingAnswer(notification)}
      ${pendingReading(notification)
        ? html`
            <div class="rounded-2xl border border-info/40 bg-info/5 mt-4 p-5">
              <div class="text-xs font-semibold uppercase tracking-wide text-info/80 flex items-center gap-1.5">
                <x-icon class="text-sm">psychology</x-icon>So haben wir Ihre Antwort verstanden
              </div>
              <div class="text-sm mt-2">${notification.answer?.interpretation?.summary}</div>
              <p class="text-xs text-base-content/50 mt-1">
                Wir sind uns nicht sicher genug, um das ohne Ihr Zeichen zu übernehmen.
              </p>
              <div class="flex gap-2 mt-3">
                <button class="btn btn-sm btn-primary" ?disabled=${busy} @click=${() => ctx.onInterpretation(true)}>
                  Stimmt, übernehmen
                </button>
                <button class="btn btn-sm btn-ghost" ?disabled=${busy} @click=${() => ctx.onInterpretation(false)}>
                  Nein, anders
                </button>
              </div>
            </div>
          `
        : isClosed(notification)
          ? nothing
          : NotificationAnswerForm({ notification, busy, onSubmit: ctx.onAnswer })}

      <div class="flex flex-wrap items-center gap-2 mt-5">
        ${notification.mailID
          ? html`<a href="/mails/${notification.mailID}" class="btn btn-ghost btn-sm gap-2">
              <x-icon class="text-base">open_in_new</x-icon>Anfrage öffnen
            </a>`
          : nothing}
        ${isClosed(notification)
          ? nothing
          : html`
              <button
                class="btn btn-ghost btn-sm gap-2"
                ?disabled=${busy}
                @click=${() => ctx.onStatus(NotificationStatus.RESOLVED)}
              >
                <x-icon class="text-base">check</x-icon>Erledigt
              </button>
              <button
                class="btn btn-ghost btn-sm gap-2 text-base-content/60"
                ?disabled=${busy}
                title="Nicht relevant — taucht auch bei erneuter Extraktion nicht wieder auf"
                @click=${() => ctx.onStatus(NotificationStatus.DISMISSED)}
              >
                <x-icon class="text-base">close</x-icon>Verwerfen
              </button>
            `}
      </div>
    </div>
  `;
}

export const NotificationsPage = stateful(() => {
  const [showDone, setShowDone] = useState(false);
  // Administrators only, off by default — the personal list is what the page is for.
  const [allUsers, setAllUsers] = useState(false);
  const [selectedID, setSelectedID] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { value: mayManageUsers } = useQuery(() => mayIManageUsers()(), []);

  const { value: notifications, loading } = useQuery(
    () =>
      allUsers
        ? subscribeAllNotifications(showDone, 0, 200)(SELECTION_SET)
        : subscribeMyNotifications(showDone, 0, 200)(SELECTION_SET),
    [showDone, allUsers],
  );
  const list = notifications ?? [];
  const selected = list.find(notification => notification._id === selectedID) ?? null;
  const myUserID = currentUserSignal.value?._id;

  // Opening a notification is what marks it read — but only my own, so an administrator
  // paging through the all-users view does not empty somebody else's badge.
  useEffect(() => {
    if (!selected || selected.readAt) return;
    if (selected.recipientUserID !== myUserID) return;
    void markNotificationRead(selected._id)().catch(() => undefined);
  }, [selected?._id]);

  const handleStatus = (status: NotificationStatus) => {
    if (!selected) return;
    setBusy(true);
    void setNotificationStatus(selected._id, status)()
      .then(() => XSnackbar.show(`Meldung als "${status}" abgelegt.`))
      .catch((err: Error) => XSnackbar.show('Konnte nicht gespeichert werden: ' + err.message))
      .finally(() => setBusy(false));
  };

  const handleInterpretation = (accept: boolean) => {
    if (!selected) return;
    setBusy(true);
    void confirmAnswerInterpretation(selected._id, accept)()
      .then(feedback => XSnackbar.show(feedback))
      .catch((err: Error) => XSnackbar.show('Konnte nicht gespeichert werden: ' + err.message))
      .finally(() => setBusy(false));
  };

  const handleAnswer = (optionKey: string | null, freeText: string) => {
    if (!selected) return;
    setBusy(true);
    void answerNotification(selected._id, optionKey ?? undefined, freeText || undefined)()
      .then(feedback => XSnackbar.show(feedback))
      .catch((err: Error) => XSnackbar.show('Antwort konnte nicht verarbeitet werden: ' + err.message))
      .finally(() => setBusy(false));
  };

  const detailContext: DetailContext = {
    busy,
    onStatus: handleStatus,
    onAnswer: handleAnswer,
    onInterpretation: handleInterpretation,
  };

  const tab = (done: boolean, label: string) => html`
    <button
      class="flex-1 btn btn-sm ${showDone === done ? 'btn-primary' : 'btn-ghost'}"
      @click=${() => {
        setSelectedID(null);
        setShowDone(done);
      }}
    >
      ${label}
    </button>
  `;

  /** Scope switch for administrators: the whole company's questions, not just my own. */
  const scopeTab = (all: boolean, label: string, hint: string) => html`
    <button
      class="flex-1 btn btn-xs ${allUsers === all ? 'btn-active' : 'btn-ghost'}"
      title=${hint}
      @click=${() => {
        setSelectedID(null);
        setAllUsers(all);
      }}
    >
      ${label}
    </button>
  `;

  return html`
    <div class="h-full flex min-h-0">
      <div class="w-96 shrink-0 border-r border-base-300 flex flex-col min-h-0">
        <div class="px-4 pt-5 pb-3 border-b border-base-200">
          <h1 class="text-lg font-bold tracking-tight">Rückfragen</h1>
          <p class="text-xs text-base-content/60 mt-0.5 mb-3">
            ${allUsers
              ? 'Meldungen der Plattform an alle Nutzer — Fehler und offene Angaben.'
              : 'Meldungen der Plattform an Sie — Fehler und offene Angaben.'}
          </p>
          <div class="flex gap-1">${tab(false, 'Offen')}${tab(true, 'Erledigt')}</div>
          ${mayManageUsers
            ? html`<div class="flex gap-1 mt-2">
                ${scopeTab(false, 'Meine', 'Nur Rückfragen, die an mich gerichtet sind')}
                ${scopeTab(true, 'Alle Nutzer', 'Rückfragen aller Nutzer — nur für die Benutzerverwaltung sichtbar')}
              </div>`
            : nothing}
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto">
          ${loading && !list.length
            ? html`<div class="p-6 text-sm text-base-content/50">Wird geladen…</div>`
            : groupByRequest(list).map(
                group => html`
                  ${renderGroupHeader(group, allUsers)}
                  ${group.items.map(notification =>
                    renderRow(notification, notification._id === selectedID, () => setSelectedID(notification._id)),
                  )}
                `,
              )}
        </div>
      </div>
      <div class="flex-1 min-w-0 overflow-y-auto">
        ${selected
          ? renderDetail(selected, detailContext)
          : list.length
            ? html`<div
                class="h-full flex flex-col items-center justify-center text-center gap-3 text-base-content/40 p-8"
              >
                <x-icon class="text-5xl">notifications</x-icon>
                <div class="text-base font-medium text-base-content/60">Wähle links eine Meldung</div>
              </div>`
            : emptyState(showDone, allUsers)}
      </div>
    </div>
  `;
});
